import { toast } from 'react-toastify';
import API_CONFIG from '../config/apiConfig';
import tokenService from './tokenService';
import storageService from './storageService';
import apiService from './apiService';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
    this.isInitialized = false;
    this.isCheckingAuth = false;
  }

  // Subscribe to auth state changes
  subscribe(callback) {
    this.listeners.push(callback);
    
    if (this.isInitialized) {
      setTimeout(() => callback(this.currentUser), 0);
    }
    
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners of auth state changes
  notifyListeners() {
    this.listeners.forEach((callback) => {
      try {
        callback(this.currentUser);
      } catch (error) {
      }
    });
  }

  // Update the current user's token after refresh
  async updateCurrentUserToken(newToken) {
    if (this.currentUser) {
      this.currentUser.token = newToken;
      this.notifyListeners();
    } else {
      await this.restoreSessionFromToken(newToken);
    }
  }

  // Restore user session when we have a valid token but no user data
  async restoreSessionFromToken(token) {
    try {
      tokenService.setAccessToken(token);

      const modulesResult = await apiService.getModulesAndFields();

      if (!modulesResult.success) {
        return;
      }

      const metadataResult = modulesResult.data.result || modulesResult.data;
      const userData = metadataResult?.user_data || {};

      const completeUserData = {
        token: token,
        userid: userData.id || metadataResult?.userid,
        crm_url: API_CONFIG.BASE_URL,
        username: userData.firstname && userData.lastname
          ? `${userData.firstname} ${userData.lastname}`
          : userData.username || metadataResult?.username || 'User',
        enabledModules: metadataResult?.enabled_modules || metadataResult?.enabledModules || {},
        moduleFields: metadataResult?.module_fields || metadataResult?.moduleFields || {},
        relatedTabs: metadataResult?.related_tabs || metadataResult?.relatedTabs || {},
        ...userData
      };

      await storageService.saveUser(completeUserData);

      this.currentUser = completeUserData;
      this.isInitialized = true;
      this.notifyListeners();
    } catch (error) {
    }
  }

  validateAndCleanUserData(user) {
    if (!user) return null;
    user.crm_url = API_CONFIG.BASE_URL;
    return user;
  }

  async checkAuthStatus() {
    if (this.isCheckingAuth) {
      return this.currentUser;
    }

    this.isCheckingAuth = true;

    try {
      if (this.currentUser && this.isInitialized) {
        this.isCheckingAuth = false;
        return this.currentUser;
      }

      // Check if we have valid tokens
      const accessToken = await tokenService.getValidAccessToken();
      if (!accessToken) {
        // No valid token - clear any old session data
        try {
          await storageService.deleteUser();
        } catch (clearError) {
        }

        this.currentUser = null;
        this.isInitialized = true;
        this.isCheckingAuth = false;
        this.notifyListeners();
        return null;
      }

      // Check storage for user data
      let user = await storageService.getUser();

      if (user && accessToken) {
        // Validate and clean user data
        user = this.validateAndCleanUserData(user);

        // Update user with current access token
        user.token = accessToken;

        // Save cleaned user data back
        await storageService.saveUser(user);

        this.currentUser = user;
        this.isInitialized = true;

        this.isCheckingAuth = false;
        this.notifyListeners();
        return this.currentUser;
      } else if (accessToken) {
        // Have valid token but no user data - restore session from server
        this.isCheckingAuth = false;
        await this.restoreSessionFromToken(accessToken);
        return this.currentUser;
      } else {
        // No user data and no token - clear any remaining tokens
        tokenService.clearAllTokens();

        this.currentUser = null;
        this.isInitialized = true;
        
        this.isCheckingAuth = false;
        this.notifyListeners();
        return null;
      }
      
    } catch (error) {
      
      try {
        tokenService.clearAllTokens();
        await storageService.deleteUser();
      } catch (clearError) {
      }
      
      this.currentUser = null;
      this.isInitialized = true;
      
      this.isCheckingAuth = false;
      this.notifyListeners();
      return null;
    }
  }

  async login(username, password) {
    try {
      // Clear any existing tokens before login
      tokenService.clearAllTokens();

      const loginResult = await apiService.login(username, password);

      if (!loginResult.success) {
        return {
          success: false,
          error: loginResult.error || 'Login failed'
        };
      }

      // Extract tokens from API response
      const responseData = loginResult.data.result || loginResult.data;
      const { token, user_data } = responseData;

      if (!token) {
        return { success: false, error: 'No access token received from login' };
      }

      // Store access token
      tokenService.setAccessToken(token);

      if (!user_data) {
        return { success: false, error: 'No user data received from login' };
      }

      // Get metadata
      const modulesResult = await apiService.getModulesAndFields();

      if (!modulesResult.success) {
        return {
          success: false,
          error: modulesResult.error || 'Failed to get metadata'
        };
      }

      // Extract metadata
      const metadataResult = modulesResult.data.result || modulesResult.data;

      const completeUserData = {
        token: token,
        userid: user_data.id,
        crm_url: API_CONFIG.BASE_URL,
        username: `${user_data.firstname} ${user_data.lastname}`,
        enabledModules: metadataResult?.enabled_modules || metadataResult?.enabledModules || {},
        moduleFields: metadataResult?.module_fields || metadataResult?.moduleFields || {},
        relatedTabs: metadataResult?.related_tabs || metadataResult?.relatedTabs || {},
        ...user_data
      };
      
      await storageService.saveUser(completeUserData);
      
      this.currentUser = completeUserData;
      this.isInitialized = true;
      
      this.notifyListeners();
      
      return { success: true };
      
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async loginWithOffice365() {
    try {
      // Clear any existing tokens before login
      tokenService.clearAllTokens();
      
      const office365Service = require('./office365Service').default;
      
      // Check if Office 365 SSO is available
      if (!office365Service.isSSoAvailable()) {
        return { 
          success: false, 
          error: 'Office 365 Single Sign-On is not available in this environment' 
        };
      }

      // Get Office 365 user info and token
      const office365Result = await office365Service.getOffice365UserInfo();
      
      if (!office365Result.success) {
        return { 
          success: false, 
          error: office365Result.error || 'Failed to get Office 365 user information' 
        };
      }

      const { accessToken, profile } = office365Result.data;

      // Send to server for authentication
      const loginResult = await apiService.loginWithOffice365(profile, accessToken);
      
      if (!loginResult.success) {
        return { 
          success: false, 
          error: loginResult.error || loginResult.data?.message || 'Office 365 login failed' 
        };
      }

      // Extract tokens from API response
      const responseData = loginResult.data.result || loginResult.data;
      const { token, user_data } = responseData;

      if (!token) {
        return { success: false, error: 'No access token received from Office 365 login' };
      }

      // Store access token
      tokenService.setAccessToken(token);

      if (!user_data) {
        return { success: false, error: 'No user data received from Office 365 login' };
      }

      // Get metadata
      const modulesResult = await apiService.getModulesAndFields();
      
      if (!modulesResult.success) {
        return { 
          success: false, 
          error: modulesResult.error || 'Failed to get metadata' 
        };
      }

      const metadataResult = modulesResult.data.result || modulesResult.data;
      
      const completeUserData = {
        token: token,
        userid: user_data.id,
        crm_url: API_CONFIG.BASE_URL,
        username: `${user_data.firstname} ${user_data.lastname}`,
        enabledModules: metadataResult?.enabled_modules || metadataResult?.enabledModules || {},
        moduleFields: metadataResult?.module_fields || metadataResult?.moduleFields || {},
        relatedTabs: metadataResult?.related_tabs || metadataResult?.relatedTabs || {},
        loginMethod: 'office365',
        ...user_data
      };
      
      await storageService.saveUser(completeUserData);
      
      this.currentUser = completeUserData;
      this.isInitialized = true;
      
      this.notifyListeners();
      
      return { success: true };
      
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async logout() {
    try {
      // Call logout API
      await apiService.logout();

      // Clear all local data
      tokenService.clearAllTokens();
      await storageService.deleteUser();

      // Clear any sessionStorage or localStorage that might have been used
      try {
        sessionStorage.clear();
        localStorage.clear();
      } catch (e) {}

      this.currentUser = null;
      this.isInitialized = true;
      this.notifyListeners();

    } catch (error) {

      // Even if API call fails, clear local data
      try {
        tokenService.clearAllTokens();
        await storageService.deleteUser();
        sessionStorage.clear();
        localStorage.clear();
        this.currentUser = null;
        this.isInitialized = true;
        this.notifyListeners();
      } catch (clearError) {
      }
    }
  }

  // Getters
  getCurrentUser() {
    return this.currentUser;
  }

  isAuthenticated() {
    return !!this.currentUser && tokenService.isAuthenticated();
  }

  getEnabledModules() {
    return this.currentUser?.enabledModules || {};
  }

  getModuleFields(module) {
    const moduleFields = this.currentUser?.moduleFields || {};
    return moduleFields[module] || [];
  }

  getAllModuleFields() {
    return this.currentUser?.moduleFields || {};
  }

  getRelatedTabs(module) {
    const relatedTabs = this.currentUser?.relatedTabs || {};
    return relatedTabs[module] || relatedTabs[module.toLowerCase()] || [];
  }

  getAllRelatedTabs() {
    return this.currentUser?.relatedTabs || {};
  }
}

export default new AuthService();